import { ProviderParams } from 'leaflet-geosearch/dist/providers/provider';
import { StyleProcessResults } from '../interfaces/OnTheMap';
import { ProviderClass } from '../interfaces/ProviderClass';
import { ResultsGeoJson, FeaturesGeoJson, ResponseGeneral } from '../interfaces/Responses';
import OnTheMap from './onthemap';

export class BaiduMap implements ProviderClass {
  /**
   * Array of BMap.Marker with property otm_id used to get the tighe marker when we want
   * open a specific infoWindow
   */
  private _markers: Array<BMap.Marker & { otm_id?: string }> = [];

  /**
   * [Baidu map object] {@link BMap.Map}
   */
  private _bmap: BMap.Map;

  /**
   * Options with properties used in functionality
   * For details see {@link OnTheMap} class
   */
  private _otm: OnTheMap;

  /**
   * custom event for trigger infoWindowDomReady
   */
  private _infoDomReadyEvent: CustomEvent;

  constructor(otm: OnTheMap) {
    this._otm = otm;
  }

  /**
   * Set map and add controls and copyright
   * @access private
   * @param {BMap.Map} map
   */
  setupMap(map: BMap.Map): void {
    map.enableScrollWheelZoom(); // enable mouse zoom
    // add navigation zoom control
    map.addControl(
      new BMap.NavigationControl({
        anchor: BMAP_ANCHOR_BOTTOM_RIGHT,
        offset: new BMap.Size(0, 20),
      })
    );

    // add copyright
    const copyControl: BMap.CopyrightControl = new BMap.CopyrightControl({
      anchor: BMAP_ANCHOR_BOTTOM_LEFT,
      offset: new BMap.Size(85, 20),
    });
    map.addControl(copyControl); //add control object
    const bs: BMap.Bounds = map.getBounds(); //get the bounds of the map
    copyControl.addCopyright({
      id: 1,
      content:
        "<a href='https://onthemap.io/experience' target='_blank' style='font-size:11px;color:#000;padding-left: 5px; border-left: 1px solid #000; text-decoration: none;'>OnTheMap</a>",
      bounds: bs,
    });

    this._bmap = map;
  }

  /**
   * add tada results to map
   * @access private
   * @param {ResultsGeoJson} data
   * @param {boolean} clear
   */
  addData(data: ResultsGeoJson, clear: boolean): void {
    // throw new Error('Method not implemented.');
    if (clear) {
      this.clearMap();
    }
    const featArray: Array<FeaturesGeoJson> = data.features;
    const bounds: BMap.Point[] = [];

    featArray.forEach((feature: FeaturesGeoJson, index: number) => {
      const type: string = feature.geometry.type;
      const position: BMap.Point = new BMap.Point(feature.geometry.coordinates[0], feature.geometry.coordinates[1]);

      if (type === 'Point') {
        // add process style for custom icon
        if (this._otm.styleProcess) {
          const iconsObj: StyleProcessResults = this._otm.styleProcess(feature, index);

          if (iconsObj.default as BMap.MarkerOptions) {
            const marker: BMap.Marker & { otm_id?: string; feature?: FeaturesGeoJson } = Object.assign(
              new BMap.Marker(position, iconsObj.default as BMap.MarkerOptions),
              {
                otm_id: feature.properties.otm_id,
                feature,
              }
            );

            if (iconsObj.hover) {
              marker.addEventListener('mouseover', () => {
                if (!this._otm.infoWindowActive) marker.setIcon(iconsObj.hover as BMap.Icon);
              });
              marker.addEventListener('mouseout', () => {
                if (!this._otm.infoWindowActive) {
                  marker.setIcon((iconsObj.default as BMap.MarkerOptions).icon as BMap.Icon);
                }
              });
            }

            if (this._otm.infoWindowProcess) {
              marker.addEventListener('click', () => {
                this.setInfoWindowContent(feature);
              });
            }
            // add the marker to map
            this._bmap.addOverlay(marker);
            // add the markers to array of markers
            this._markers.push(marker);
            // add position point to array
            bounds.push(position);
          } else {
            console.error('Baidu map addData: no default marker state provided');
            return;
          }
        }
      }
    });
    // set the viewport
    this._bmap.setViewport(bounds);
    // set the featurecolletion of onthemap,
    // @todo this should be REVIEWED
    this._otm.featureCollection = featArray;
  }

  /**
   * set the content of the infowindow
   * @access private
   * @param {FeaturesGeoJson} feat - result object
   */
  setInfoWindowContent(feat: FeaturesGeoJson): void {
    const content: string = this._otm.infoWindowProcess(feat) || '';
    const infoW: BMap.InfoWindow = new BMap.InfoWindow(content);
    const position: BMap.Point = new BMap.Point(feat.geometry.coordinates[0], feat.geometry.coordinates[1]);

    // if there is an infowindow open somewhere
    this.closeInfoWindow();

    if (this._otm.options.autoOpenInfoWindow) {
      this._bmap.openInfoWindow(infoW, new BMap.Point(position.lng, position.lat));

      // set infoWindowActive
      this._otm.infoWindowActive = infoW;

      const iconsObj: StyleProcessResults = this._otm.styleProcess(feat);

      /** manage active style on marker */
      if (iconsObj.active) {
        const markerToManage: BMap.Marker[] = this._markers.filter(
          (marker: BMap.Marker & { otm_id?: string; feature?: FeaturesGeoJson }) => {
            return marker.otm_id === feat.properties?.otm_id;
          }
        );

        if (markerToManage.length > 1) {
          console.error('Baidu map setInfoWindowContent: no marker to activated');
          return;
        } else {
          markerToManage[0].setIcon(iconsObj.active as BMap.Icon);
          infoW.addEventListener('close', () => {
            markerToManage[0].setIcon((iconsObj.default as BMap.MarkerOptions).icon as BMap.Icon);
            this._otm.infoWindowActive = undefined;
            infoW.removeEventListener('close', () => {});
          });
        }
      }
    }

    /** dispatch the infoWoindowDomReady event */
    this._infoDomReadyEvent = new CustomEvent('baidu.infoWindowDomReady', { detail: feat });
    window.dispatchEvent(this._infoDomReadyEvent);
  }

  /**
   * clear map from results
   * @access private
   */
  clearMap(): void {
    this._bmap.clearOverlays();
    this._markers = [];
    this.closeInfoWindow();
  }

  /**
   * focus map on results
   * @access private
   * @param {FeaturesGeoJson[]} featureCollection - results collection
   */
  focus(featureCollection: FeaturesGeoJson[]): void {
    const bounds: BMap.Point[] = [];

    featureCollection.forEach((feature) => {
      const latlng: BMap.Point = new BMap.Point(feature.geometry?.coordinates[0], feature.geometry?.coordinates[1]);
      bounds.push(latlng);
    });

    this._bmap.setViewport(bounds);
  }

  /**
   * open info window of specific id
   * @access private
   * @param {string} otm_id - id otm
   * @param {FeaturesGeoJson[]} featureCollection - collections of results
   */
  openInfoWindow(otm_id: string, featureCollection: FeaturesGeoJson[]): void {
    const featToOpen: FeaturesGeoJson[] = featureCollection.filter((feat: FeaturesGeoJson) => {
      return feat.properties?.otm_id === otm_id;
    });
    const markerToOpen: Array<BMap.Marker & { otm_id?: string }> = this._markers.filter((marker: BMap.Marker & { otm_id?: string }) => {
      return marker.otm_id === otm_id;
    });

    if (markerToOpen.length === 1 && featToOpen.length === 1) {
      this.setInfoWindowContent(featToOpen[0]);
    }
  }

  /**
   * close the infoWindowActive
   * @access private
   */
  private closeInfoWindow(): void {
    if (this._otm.infoWindowActive) {
      this._bmap.closeInfoWindow();
      this._otm.infoWindowActive = undefined;
    }
  }

  setAutocomplete(container: HTMLInputElement, options: BMap.AutocompleteOptions, callback: Function): void {
    const autocomplete: BMap.Autocomplete = new BMap.Autocomplete({
      input: container,
      location: this._bmap,
      onSearchComplete: (result: BMap.AutocompleteResult) => {
        if (result) {
          autocomplete.show();
        } else {
          autocomplete.hide();
        }
      },
    });

    autocomplete.onconfirm = (event: { type: string; item: { index: number; value: BMap.AutocompleteResultPoi }; target: string }) => {
      // console.log('confirm event', event);
      const value: BMap.AutocompleteResultPoi = event?.item?.value;
      const completeAddress = `${value.province || ''}${value.City || ''}${value.district || ''}${value.street || ''}${
        value.business || ''
      }`; // fix issu when some attributes in value are empty

      let output: { value: string; lat: number; lng: number };
      // console.log('complete address', completeAddress);
      // console.log('value city', value.city);

      const geocoder: BMap.Geocoder = new BMap.Geocoder();
      geocoder.getPoint(
        completeAddress,
        (point: BMap.Point) => {
          if (point) {
            output = {
              value: completeAddress,
              lat: point.lat,
              lng: point.lng,
            };
          } else {
            console.error('Wrong address georeferencing');
          }

          if (callback && typeof callback == 'function') {
            callback(output);
          }
        },
        value.City
      );
    };
  }

  distanceMatrixSearch(data: ResponseGeneral, originPosition: google.maps.LatLngLiteral, callback?: Function): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Geocode from partial address to address complete
   * @private
   * @param value
   * @param {object} [options] - object where pass options
   * @param {string} [options.city = ''] - city name, default is ''
   * @param callback
   */
  geocode(value: string, options?: { city?: string }, callback?: Function): void {
    const geocoder: BMap.Geocoder = new BMap.Geocoder();
    const city: string = (options?.city as string) ? (options.city as string) : '';

    let output: BMap.GeocoderResult[] = new Array();

    geocoder.getPoint(
      value,
      (point: BMap.Point) => {
        if (point) {
          // if point is found
          geocoder.getLocation(point, (address: BMap.GeocoderResult) => {
            if (address) {
              output.push(address);
            }
            if (callback && typeof callback == 'function') {
              callback(output);
            }
          });
        } else {
          console.error('Address not found');
          if (callback && typeof callback == 'function') {
            callback(output);
          }
        }
      },
      city // qui ci andrebbe la città per la ricerca ma come è possibile estrarla?
    );
  }
}
